<template>
  <div
    class="relative inline-grid place-items-center"
    :style="{ width: `${size}px`, height: `${size}px` }"
    role="img"
    :aria-label="`DMARC pass rate ${dmarc} percent, DKIM pass rate ${dkim} percent`"
  >
    <!-- The whole svg is rotated -90deg so both arcs start at twelve o'clock. The tick ring
         is radial, so rotating it changes nothing. -->
    <svg :viewBox="`0 0 ${size} ${size}`" class="h-full w-full -rotate-90" aria-hidden="true">
      <circle :cx="c" :cy="c" :r="outerR" fill="none" class="gauge-track" :stroke-width="outerStroke" />
      <circle :cx="c" :cy="c" :r="innerR" fill="none" class="gauge-track" :stroke-width="innerStroke" />

      <circle
        :cx="c"
        :cy="c"
        :r="outerR"
        fill="none"
        class="gauge-arc-beam"
        :stroke-width="outerStroke"
        stroke-linecap="round"
        :stroke-dasharray="outerCircumference"
        :stroke-dashoffset="outerOffset"
      />
      <circle
        :cx="c"
        :cy="c"
        :r="innerR"
        fill="none"
        class="gauge-arc-plum"
        :stroke-width="innerStroke"
        stroke-linecap="round"
        :stroke-dasharray="innerCircumference"
        :stroke-dashoffset="innerOffset"
      />

      <line
        v-for="(tick, index) in ticks"
        :key="index"
        :x1="tick.x1"
        :y1="tick.y1"
        :x2="tick.x2"
        :y2="tick.y2"
        class="gauge-tick"
        :stroke-width="tick.long ? 1.5 : 1"
      />
    </svg>

    <div class="absolute inset-0 flex flex-col items-center justify-center text-center">
      <p class="cr-label">Trust index</p>
      <p class="mt-0.5 flex items-baseline justify-center nums">
        <span class="text-5xl font-semibold leading-none tracking-tight text-gray-900">{{ dmarc }}</span>
        <span class="ml-0.5 text-lg font-medium text-gray-400">%</span>
      </p>
      <p class="cr-label mt-1.5">DMARC pass</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

/**
 * The dashboard's trust index: one full-circle track per protocol layer, drawn with round
 * caps so the ends read as soft, and a dial of ticks between the inner arc and the number
 * so the ring still has a pointy texture at small sizes.
 *
 * Values are percentages; 0 draws nothing, 100 closes the ring.
 */
const props = withDefaults(
  defineProps<{ dmarc: number; dkim: number; size?: number }>(),
  { size: 208 },
);

const clamp = (value: number) => Math.min(100, Math.max(0, value));

const c = computed(() => props.size / 2);
const outerR = computed(() => props.size / 2 - 12);
const innerR = computed(() => props.size / 2 - 31);
const outerStroke = 11;
const innerStroke = 9;

const circumference = (radius: number) => 2 * Math.PI * radius;
const outerCircumference = computed(() => circumference(outerR.value));
const innerCircumference = computed(() => circumference(innerR.value));
const outerOffset = computed(() => outerCircumference.value * (1 - clamp(props.dmarc) / 100));
const innerOffset = computed(() => innerCircumference.value * (1 - clamp(props.dkim) / 100));

// 44 ticks, every fourth one longer. Radii sit between the inner arc and the number.
const ticks = computed(() => {
  const count = 44;
  return Array.from({ length: count }, (_, index) => {
    const angle = (index / count) * Math.PI * 2;
    const long = index % 4 === 0;
    const r1 = props.size / 2 - 44;
    const r2 = props.size / 2 - (long ? 39 : 41.5);
    return {
      long,
      x1: c.value + r1 * Math.cos(angle),
      y1: c.value + r1 * Math.sin(angle),
      x2: c.value + r2 * Math.cos(angle),
      y2: c.value + r2 * Math.sin(angle),
    };
  });
});
</script>

<style scoped>
@reference "../style.css";

.gauge-track {
  stroke: rgb(var(--ramp-gray-200));
}

/* The two signal ramps live on the dashboard root (see DashboardView.vue) and are swapped
   for the dark theme there; this component only consumes them. */
.gauge-arc-beam {
  stroke: rgb(var(--cr-beam));
  transition: stroke-dashoffset 700ms cubic-bezier(0.22, 1, 0.36, 1);
  filter: drop-shadow(0 0 6px rgb(var(--cr-beam) / 0.45));
}

.gauge-arc-plum {
  stroke: rgb(var(--cr-plum));
  transition: stroke-dashoffset 700ms cubic-bezier(0.22, 1, 0.36, 1);
  filter: drop-shadow(0 0 6px rgb(var(--cr-plum) / 0.4));
}

.gauge-tick {
  stroke: rgb(var(--ramp-gray-400));
}

@media (prefers-reduced-motion: reduce) {
  .gauge-arc-beam,
  .gauge-arc-plum {
    transition: none;
  }
}
</style>
